import React, { useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Alert,
} from "@mui/material";
import OrbLoader from "../components/common/OrbLoader";
import { useGetDataModelMappingsQuery } from "../store/apis/dataModelApi";
import { useSelector } from "react-redux";
import { selectSidebarCollapsed } from "../store/slices/app/appSelector";
import mermaid from "mermaid";
import { useThemeMode } from "../theme/ThemeModeProvider";
import PageBreadcrumbs from "../components/layout/PageBreadcrumbs";
import PageContainer from "../components/layout/PageContainer";

const DataModel: React.FC = () => {
  const theme = useTheme();
  const { mode } = useThemeMode();
  const sidebarCollapsed = useSelector(selectSidebarCollapsed);
  const { data, isLoading, error } = useGetDataModelMappingsQuery();
  const mermaidRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (data?.mermaidDiagram && mermaidRef.current) {
      mermaid.initialize({
        startOnLoad: true,
        theme: mode === "dark" ? "dark" : "default",
      });
      mermaid.contentLoaded();
    }
  }, [data?.mermaidDiagram, mode]);

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 4,
          ml: { xs: 0, md: sidebarCollapsed ? "64px" : "250px" },
          transition: "margin-left 0.3s ease",
          width: {
            xs: "100%",
            md: sidebarCollapsed ? "calc(100% - 64px)" : "calc(100% - 250px)",
          },
        }}
      >
        <PageBreadcrumbs />

        <PageContainer>
          <Typography variant="h4" fontWeight="700" mb={4} color="primary.main">
            Data Model Integrations
          </Typography>

          {isLoading && (
            <Box display="flex" justifyContent="center" mt={10}>
              <OrbLoader size="large" />
            </Box>
          )}

          {error && (
            <Alert severity="error">
              Failed to load data model bindings from API.
            </Alert>
          )}

          {data && (
            <>
              <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Architecture & Aggregation Flow
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                  Observe how standard Campaign models map internally through
                  merges into distinct integration platforms.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    p: 2,
                    overflowX: "auto",
                  }}
                >
                  <div className="mermaid" ref={mermaidRef} key={mode}>
                    {data.mermaidDiagram}
                  </div>
                </Box>
              </Paper>

              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Platform Storage Hierarchy Details
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow
                        sx={{ backgroundColor: theme.palette.grey[100] }}
                      >
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Platform
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Database Tables Utilized
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Integration Description
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.platforms.map((platform, index) => (
                        <TableRow key={index} hover>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {platform.platformName}
                          </TableCell>
                          <TableCell>
                            {platform.storageHierarchy.map((table, i) => (
                              <Box
                                component="span"
                                key={i}
                                sx={{
                                  display: "inline-block",
                                  backgroundColor: theme.palette.primary.light,
                                  color: "white",
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontSize: "0.75rem",
                                  mr: 1,
                                  mb: 1,
                                }}
                              >
                                {table}
                              </Box>
                            ))}
                          </TableCell>
                          <TableCell
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            {platform.description}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}
        </PageContainer>
      </Box>
    </Box>
  );
};

export default DataModel;
